import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost, apiPut } from '../../../api/client';
import { PageHeader } from '../../../components/common/PageHeader';
import { SelectField } from '../../../components/fields/SelectField';
import { PermissionGate } from '../../../components/auth/PermissionGate';
import { useAuthStore } from '../../../store/auth.store';
import { ReasonDialog } from './ReasonDialog';
import { EvidencePicker, EvidenceFilesDialog } from '../evidence';
import type { EvidenceItem } from '../evidence/evidenceLogic';
import { APPROVAL_REASON_LABEL, RECEIPT_SOURCES, RECEIPT_STATUS_LABEL, ReceiptAction, isMoney, receiptActions, receiptFormError, statusColor } from './expenseLogic';

// Branch Cash - manual cash receipts (P3): cash that arrives other than through a sale (sales cash is imported automatically and is
// never entered here). A routine receipt is confirmed at once; a large one, or one from a source the company marked sensitive, waits
// for a different person. Buttons follow permissions and state; the server decides.

const unwrap = (d: any) => d?.data ?? d;
const errMsg = (e: any, fallback: string) => e?.response?.data?.error ?? fallback;
const rupees = (n: number | null | undefined) => (n === null || n === undefined ? '-' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const SOURCE_LABEL: Record<string, string> = Object.fromEntries(RECEIPT_SOURCES.map((s) => [s.value, s.label]));
const REFRESH = ['bc-receipts', 'bc-rcpt-pending', 'bc-summary', 'bc-moves', 'bc-blockers'];

function RulesPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const rulesQ = useQuery({ queryKey: ['bc-rcpt-rules'], queryFn: async () => unwrap(await apiGet('/branch-cash/receipts/rules')) as any[] });
  const [threshold, setThreshold] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (p: { source: string; body: object }) => apiPut(`/branch-cash/receipts/rules/${p.source}`, p.body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bc-rcpt-rules'] }); enqueueSnackbar('Rule saved', { variant: 'success' }); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not save the rule'), { variant: 'error' }),
  });
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Approval rules by source</Typography>
      <Typography variant="caption" color="text.secondary">Without a rule, a receipt needs approval only at or above the branch's receipt threshold.</Typography>
      <Table size="small">
        <TableHead><TableRow><TableCell>Source</TableCell><TableCell>Always needs approval</TableCell><TableCell>Own threshold (₹)</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>{(rulesQ.data ?? []).map((r: any) => (
          <TableRow key={r.source}>
            <TableCell>{SOURCE_LABEL[r.source] ?? r.source}</TableCell>
            <TableCell><SelectField size="small" label="" value={r.approvalRequired ? 'yes' : 'no'} onChange={(e) => save.mutate({ source: r.source, body: { approvalRequired: e.target.value === 'yes' } })}
              options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]} /></TableCell>
            <TableCell><TextField size="small" value={threshold[r.source] ?? (r.approvalThreshold ?? '')} onChange={(e) => setThreshold((p) => ({ ...p, [r.source]: e.target.value }))} /></TableCell>
            <TableCell><Button size="small" disabled={save.isPending || ((threshold[r.source] ?? '') !== '' && !isMoney(threshold[r.source]))}
              onClick={() => save.mutate({ source: r.source, body: { approvalThreshold: (threshold[r.source] ?? '').trim() === '' ? null : threshold[r.source].trim() } })}>Save threshold</Button></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </Paper>
  );
}

export function ReceiptsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user?.id);
  const has = useAuthStore((s) => s.hasPermission);
  const settingsQ = useQuery({ queryKey: ['bc-settings'], queryFn: async () => unwrap(await apiGet('/branch-cash/settings')) as any[] });
  const branches = (settingsQ.data ?? []) as any[];
  const [branchId, setBranchId] = useState('');
  useEffect(() => { if (!branchId && branches.length) setBranchId(branches[0].id); }, [branches, branchId]);
  const [form, setForm] = useState({ source: 'ALLOCATION_FROM_OFFICE', amount: '', reason: '', referenceNo: '', evidenceRef: '' });
  const [dialog, setDialog] = useState<{ id: string; action: ReceiptAction } | null>(null);
  const [status, setStatus] = useState('');
  const [files, setFiles] = useState<EvidenceItem[]>([]);
  const [filesFor, setFilesFor] = useState<{ id: string; number: string } | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const listQ = useQuery({ queryKey: ['bc-receipts', branchId, status], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/receipts', { branchId, status: status || undefined, limit: 100 })) as any });
  const pendingQ = useQuery({ queryKey: ['bc-rcpt-pending', branchId], enabled: !!branchId && has('cash:receipt_approve'), queryFn: async () => unwrap(await apiGet('/branch-cash/receipts/pending-approvals', { branchId })) as any[] });
  const rows: any[] = listQ.data?.rows ?? []; const pending = pendingQ.data ?? [];
  const refresh = () => REFRESH.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const fail = (e: any) => enqueueSnackbar(errMsg(e, 'The action was refused'), { variant: 'error' });

  const create = useMutation({
    mutationFn: async () => unwrap(await apiPost('/branch-cash/receipts', { branchId, source: form.source, amount: form.amount.trim(), reason: form.reason.trim(), referenceNo: form.referenceNo.trim() || undefined, evidenceRef: form.evidenceRef.trim() || undefined, evidenceFileIds: files.length ? files.map((x) => x.id) : undefined })) as any,
    onSuccess: (r: any) => {
      refresh(); setFiles([]); setForm((p) => ({ ...p, amount: '', reason: '', referenceNo: '', evidenceRef: '' }));
      enqueueSnackbar(r.status === 'CONFIRMED' ? `${r.receiptNumber} recorded - cash added to the till` : `${r.receiptNumber} saved - awaiting approval`, { variant: 'success' });
    },
    onError: fail,
  });
  const act = useMutation({
    mutationFn: async (p: { id: string; action: string; reason?: string }) => apiPost(`/branch-cash/receipts/${p.id}/${p.action}`, p.action === 'approve' || p.action === 'reject' ? { note: p.reason } : { reason: p.reason }),
    onSuccess: () => { refresh(); setDialog(null); enqueueSnackbar('Done', { variant: 'success' }); }, onError: fail,
  });
  const error = receiptFormError(form);

  const Buttons = ({ r }: { r: any }) => (
    <Stack direction="row" spacing={0.5}>
      {receiptActions(r, me, has).map((a) => (
        <Button key={a} size="small" variant={a === 'approve' ? 'contained' : 'outlined'} color={a === 'approve' ? 'primary' : 'error'} disabled={act.isPending}
          onClick={() => (a === 'approve' ? act.mutate({ id: r.id, action: a }) : setDialog({ id: r.id, action: a }))}>{a[0].toUpperCase() + a.slice(1)}</Button>
      ))}
    </Stack>
  );
  const Reasons = ({ r }: { r: any }) => <>{(r.approvalReasons ?? []).map((x: string) => <Chip key={x} size="small" sx={{ ml: 0.5 }} variant="outlined" label={APPROVAL_REASON_LABEL[x] ?? x} />)}</>;

  return (
    <Box>
      <PageHeader title="Cash receipts" subtitle="Cash received other than through a sale - sales cash is imported automatically" />
      <Stack spacing={2}>
        <Box sx={{ maxWidth: 320 }}>
          <SelectField label="Branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} options={branches.map((b) => ({ value: b.id, label: `${b.branchCode} - ${b.name}` }))} />
        </Box>

        <PermissionGate permission="cash:receipt_record">
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Record a cash receipt</Typography>
            <form onSubmit={(e) => { e.preventDefault(); if (!error && !create.isPending) create.mutate(); }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
                <SelectField label="Source" value={form.source} onChange={set('source') as any} options={RECEIPT_SOURCES} />
                <TextField label="Amount (₹)" value={form.amount} onChange={set('amount')} InputLabelProps={{ shrink: true }} error={form.amount !== '' && !isMoney(form.amount)} />
                <TextField label="Reason" value={form.reason} onChange={set('reason')} InputLabelProps={{ shrink: true }} />
                <TextField label="Reference no." value={form.referenceNo} onChange={set('referenceNo')} InputLabelProps={{ shrink: true }} />
                <TextField label="Evidence reference" value={form.evidenceRef} onChange={set('evidenceRef')} InputLabelProps={{ shrink: true }} />
              </Box>
              {has('cash:evidence_upload') && <Box sx={{ mt: 1.5 }}><EvidencePicker branchId={branchId} ownerType="RECEIPT" items={files} onChange={setFiles} disabled={create.isPending} /></Box>}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
                <Button type="submit" variant="contained" disabled={!!error || create.isPending}>Record receipt</Button>
                {error && form.amount !== '' && <Typography variant="caption" color="error">{error}</Typography>}
              </Stack>
            </form>
            <Alert severity="info" sx={{ mt: 1.5 }}>Cash is added to the till when the receipt is confirmed - at once for a routine receipt, after another person approves it otherwise.</Alert>
          </Paper>
        </PermissionGate>

        <PermissionGate permission="cash:receipt_approve">
          {pending.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Awaiting your decision ({pending.length})</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>No.</TableCell><TableCell>Source</TableCell><TableCell>Reason</TableCell><TableCell align="right">Amount</TableCell><TableCell>Why approval</TableCell><TableCell>Decision</TableCell></TableRow></TableHead>
                  <TableBody>{pending.map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.receiptNumber}</TableCell><TableCell>{SOURCE_LABEL[r.source] ?? r.source}</TableCell><TableCell>{r.reason}</TableCell>
                      <TableCell align="right">{rupees(r.amount)}</TableCell><TableCell><Reasons r={r} /></TableCell>
                      <TableCell>{r.canDecide ? <Buttons r={r} /> : <Typography variant="caption">You recorded this - another person must decide</Typography>}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </Box>
            </Paper>
          )}
        </PermissionGate>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Receipt register</Typography>
            <Box sx={{ width: 220 }}>
              <SelectField size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} emptyLabel="All" options={Object.entries(RECEIPT_STATUS_LABEL).map(([value, label]) => ({ value, label }))} />
            </Box>
          </Stack>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow><TableCell>No.</TableCell><TableCell>Date</TableCell><TableCell>Source</TableCell><TableCell>Reason</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.receiptNumber}</TableCell><TableCell>{new Date(r.receivedAt).toLocaleDateString('en-IN')}</TableCell><TableCell>{SOURCE_LABEL[r.source] ?? r.source}</TableCell>
                    <TableCell>{r.reason}{r.status === 'REJECTED' && r.decisionNote ? ` - rejected: ${r.decisionNote}` : ''}
                      {r.evidenceFileCount > 0 && <Button size="small" sx={{ ml: 1 }} onClick={() => setFilesFor({ id: r.id, number: r.receiptNumber })}>Files ({r.evidenceFileCount})</Button>}
                    </TableCell>
                    <TableCell align="right">{rupees(r.amount)}</TableCell>
                    <TableCell><Chip size="small" color={statusColor(r.status)} label={RECEIPT_STATUS_LABEL[r.status] ?? r.status} /><Reasons r={r} /></TableCell>
                    <TableCell><Buttons r={r} /></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No receipts.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <PermissionGate permission="cash:config_limits"><RulesPanel /></PermissionGate>
      </Stack>

      <EvidenceFilesDialog open={!!filesFor} ownerType="RECEIPT" ownerId={filesFor?.id ?? null} title={`Files attached to ${filesFor?.number ?? ''}`} onClose={() => setFilesFor(null)} />
      <ReasonDialog open={!!dialog} busy={act.isPending} onClose={() => setDialog(null)}
        title={dialog?.action === 'reject' ? 'Reject receipt' : dialog?.action === 'cancel' ? 'Cancel receipt' : 'Reverse receipt'}
        message={dialog?.action === 'reverse' ? 'The cash comes back out of the till. This is recorded and cannot be undone.' : undefined}
        confirmLabel={dialog?.action === 'reject' ? 'Reject' : dialog?.action === 'cancel' ? 'Cancel receipt' : 'Reverse'}
        onConfirm={(reason) => dialog && act.mutate({ id: dialog.id, action: dialog.action, reason })} />
    </Box>
  );
}
